import { useEffect, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { isLoggedIn } from '@gn-audio/jss-core';

import { LoginTypeEnum, LoginTypeValue } from '../../types';
import { useAuthReady } from '../contexts';

type UseLoginTypeResult = {
  isVisible: boolean;
  userIsLoggedIn: boolean;
};

/**
 * Custom React hook to determine the visibility of a login-related UI element
 * based on the current login type and the user's authentication status.
 *
 * @param loginType - Optional field containing the current login type value.
 * @returns An object containing:
 *   - `isVisible`: A boolean indicating if the UI element should be visible.
 *   - `userIsLoggedIn`: A boolean indicating if the user is currently logged in.
 *
 * The hook checks the user's login status when the `loginType` changes and
 * updates the visibility logic accordingly.
 */
export const useLoginType = (loginType?: Field<LoginTypeValue>): UseLoginTypeResult => {
  const [userIsLoggedIn, setUserIsLoggedIn] = useState<boolean>(false);
  const { authReady } = useAuthReady();

  useEffect(() => {
    if (!loginType?.value || !authReady) {
      return;
    }
    const checkLoginStatus = async () => {
      try {
        const loggedIn = await isLoggedIn();
        setUserIsLoggedIn(loggedIn);
      } catch (error) {
        console.error('Error checking login status:', error);
      }
    };

    checkLoginStatus();
  }, [loginType, authReady]);

  const isVisible =
    !loginType?.value ||
    !(
      (loginType.value === LoginTypeEnum.requireLogin && !userIsLoggedIn) ||
      (loginType.value === LoginTypeEnum.hideLogin && userIsLoggedIn)
    );

  return {
    isVisible,
    userIsLoggedIn,
  };
};
